import { useState, type FormEvent } from 'react';
import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Input, useToast } from '@vd/ui';
import {
  getWalkthroughVideo,
  resetWalkthroughVideo,
  setWalkthroughVideo,
  WALKTHROUGH_VIDEO_KEY,
  type WalkthroughVideoSetting,
} from '../../features/onboarding/walkthroughVideoApi';
import styles from './Admin.module.css';

/** Every change to the walkthrough video, saves and resets alike, under one key. */
const CHANGING = ['walkthrough-video', 'change'] as const;

/**
 * Admin → Content (TOADO-983): what Toado shows everyone that staff change
 * without a deploy. For now, the walkthrough video on Home, set from a Vimeo
 * or YouTube link; the server reads the link and refuses anything else.
 */
export function ContentPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data } = useQuery({ queryKey: WALKTHROUGH_VIDEO_KEY, queryFn: getWalkthroughVideo });
  const [link, setLink] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  /**
   * The change, and the cache made to say so, in one step: done inside the
   * mutation rather than in a callback of this page, so it happens whether or
   * not the page is still open when the answer comes (z.ai round 3). A read
   * that left before the change is cancelled first, or it would land after and
   * put the old video back (Codex round 3).
   */
  const changing = (send: () => Promise<WalkthroughVideoSetting>) => async () => {
    const setting = await send();
    await qc.cancelQueries({ queryKey: WALKTHROUGH_VIDEO_KEY });
    qc.setQueryData(WALKTHROUGH_VIDEO_KEY, setting);
    return setting;
  };
  const done = (title: string) => {
    setLink('');
    setProblem(null);
    toast.push({ variant: 'success', title });
  };
  // Said in a toast as well as on the page, since a change can end after its
  // page has gone and come back, and that page never hears of it (Codex round 3).
  const failed = (error: unknown) => {
    const message = error instanceof Error ? error.message : 'The video could not be changed.';
    setProblem(message);
    toast.push({ variant: 'danger', title: 'Walkthrough video not changed', description: message });
  };
  const save = useMutation({
    mutationKey: CHANGING,
    mutationFn: (url: string) => changing(() => setWalkthroughVideo(url))(),
    onSuccess: () => done('Walkthrough video changed'),
    onError: failed,
  });
  const reset = useMutation({
    mutationKey: CHANGING,
    mutationFn: changing(resetWalkthroughVideo),
    onSuccess: () => done('Built-in walkthrough video back'),
    onError: failed,
  });
  // One change at a time: a save still on its way would land after a reset made
  // meanwhile and undo it (Codex round 1). Counted in the query client, not in
  // this page, so a change started before leaving Content still holds the
  // buttons on coming back (Codex round 2).
  const busy = useIsMutating({ mutationKey: CHANGING }) > 0;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (link.trim() && !busy) save.mutate(link.trim());
  };
  const video = data?.video;
  return (
    <>
      <div className={styles.pageHeader}>
        <h1 className={styles.h1}>Content</h1>
      </div>
      <p className={styles.sub}>What everyone sees that you can change here without a deploy.</p>

      <section className={styles.section} aria-labelledby="walkthrough-video-heading">
        <h2 id="walkthrough-video-heading" style={{ fontSize: 'var(--fs-lg)', marginBottom: 8 }}>Walkthrough video on Home</h2>
        {video && (
          <p className={styles.muted} data-testid="walkthrough-video-current" style={{ marginBottom: 12 }}>
            {data.custom ? 'Showing ' : 'Showing the built-in video, '}
            <a href={video.watchUrl} target="_blank" rel="noopener noreferrer">{video.watchUrl}</a>
            {video.thumbnailUrl ? '' : '. Home shows a plain play button, since no still was found for it.'}
          </p>
        )}
        <form onSubmit={submit} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <Input
            style={{ flex: '1 1 320px', maxWidth: 520 }}
            type="text"
            spellCheck={false}
            autoComplete="off"
            inputMode="url"
            placeholder="https://vimeo.com/… or https://youtu.be/…"
            aria-label="Vimeo or YouTube link"
            aria-describedby={problem ? 'walkthrough-video-problem' : undefined}
            value={link}
            onChange={(event) => setLink(event.target.value)}
          />
          <Button type="submit" variant="primary" disabled={!link.trim() || busy}>Use this video</Button>
          {data?.custom && (
            <Button type="button" variant="secondary" disabled={busy} onClick={() => { if (!busy) reset.mutate(); }}>
              Use the built-in video
            </Button>
          )}
        </form>
        {problem && <p id="walkthrough-video-problem" role="alert" style={{ color: 'var(--danger)', marginTop: 8 }}>{problem}</p>}
      </section>
    </>
  );
}
